import * as Dialog from "@radix-ui/react-dialog";
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { uploadEntityDocument } from "../../lib/dataFetchers";
import { DOCUMENTS_ALLOWED_EXTENSIONS, DOCUMENTS_MAX_SIZE_MB } from "../../lib/types";
import { cn } from "../../lib/cn";
import { formatBytes } from "../../lib/formatBytes";

interface UploadDocumentDialogProps {
  entityId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Extract lowercase file extension including the leading dot. */
function getExtension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot === -1) return "";
  return filename.slice(dot).toLowerCase();
}

export function UploadDocumentDialog({
  entityId,
  open,
  onOpenChange,
}: UploadDocumentDialogProps): JSX.Element {
  const { t } = useTranslation(["documents", "common"]);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);

  // Validation errors (inline, not toast)
  const [errors, setErrors] = useState<{
    name?: string;
    file?: string;
  }>({});

  function reset() {
    setName("");
    setNotes("");
    setSelectedFile(null);
    setUploadPercent(0);
    setErrors({});
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function handleClose() {
    if (!mutation.isPending) {
      reset();
      onOpenChange(false);
    }
  }

  /** Validate a candidate file and update the `file` error. Returns true if valid. */
  function validateFile(file: File): boolean {
    const ext = getExtension(file.name);
    if (!DOCUMENTS_ALLOWED_EXTENSIONS.has(ext)) {
      setErrors((prev) => ({ ...prev, file: t("documents:upload_validation_extension") }));
      return false;
    }
    if (file.size > DOCUMENTS_MAX_SIZE_MB * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, file: t("documents:upload_validation_size") }));
      return false;
    }
    setErrors((prev) => ({ ...prev, file: undefined }));
    return true;
  }

  function handleFileChange(file: File | null) {
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (validateFile(file)) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    handleFileChange(file);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleFileChange(file);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave() {
    setIsDragOver(false);
  }

  function validateForm(): boolean {
    const nextErrors: { name?: string; file?: string } = {};
    if (!name.trim()) {
      nextErrors.name = t("documents:upload_validation_name_required");
    }
    if (!selectedFile) {
      nextErrors.file = t("documents:upload_validation_file_required");
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  const mutation = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append("name", name.trim());
      if (notes.trim()) {
        formData.append("notes", notes.trim());
      }
      // selectedFile is guaranteed non-null here (validateForm checked)
      formData.append("file", selectedFile!);

      return uploadEntityDocument(entityId, formData, (percent) => {
        setUploadPercent(percent);
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["entity-documents", entityId] });
      void queryClient.invalidateQueries({ queryKey: ["entity", entityId] });
      void queryClient.invalidateQueries({ queryKey: ["entities"] });
      void queryClient.invalidateQueries({ queryKey: ["changelog-recent"] });
      toast.success(t("documents:upload_success"));
      reset();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (status === 413) {
        toast.error(t("documents:upload_error_too_large"));
      } else if (status === 415) {
        toast.error(t("documents:upload_error_invalid_type"));
      } else if (status === 500 && code === "documents_dir_unwritable") {
        toast.error(t("documents:upload_error_dir_unwritable"));
      } else {
        toast.error(t("documents:upload_error_generic"));
      }
      setUploadPercent(0);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;
    mutation.mutate();
  }

  // The accepted attribute string for the <input type="file">
  const acceptAttr = Array.from(DOCUMENTS_ALLOWED_EXTENSIONS).join(",");

  const isSubmitDisabled =
    mutation.isPending ||
    !name.trim() ||
    !selectedFile ||
    !!errors.file;

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "bg-surface rounded-xl border border-border shadow-card-hov",
            "p-6 w-[min(92vw,520px)] max-h-[90dvh] overflow-y-auto focus:outline-none",
          )}
          aria-describedby="upload-document-desc"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <Dialog.Title className="text-base font-semibold text-text">
                {t("documents:upload_dialog_title")}
              </Dialog.Title>
              <Dialog.Description
                id="upload-document-desc"
                className="text-xs text-text-muted mt-0.5"
              >
                {t("documents:upload_dialog_description")}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label={t("common:close")}
              onClick={handleClose}
              className="h-11 w-11 inline-flex items-center justify-center rounded-md
                         hover:bg-bg focus:outline-none focus:ring-2 focus:ring-border
                         shrink-0 ml-2"
            >
              <X className="h-5 w-5 text-text-dim" aria-hidden />
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Document name */}
            <div className="mb-4">
              <label
                htmlFor="doc-name"
                className="block text-xs font-medium text-text-muted mb-1"
              >
                {t("documents:upload_field_name")}
                <span className="text-sev-expired ml-0.5" aria-hidden>*</span>
              </label>
              <input
                id="doc-name"
                type="text"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                placeholder={t("documents:upload_field_name_placeholder")}
                className={cn(
                  "w-full h-11 px-3 rounded-md border bg-bg text-base",
                  "focus:bg-surface focus:outline-none focus:ring-2 focus:ring-accent/20",
                  errors.name
                    ? "border-sev-expired focus:border-sev-expired"
                    : "border-border focus:border-accent",
                )}
                aria-describedby={errors.name ? "doc-name-error" : undefined}
                aria-invalid={!!errors.name}
                disabled={mutation.isPending}
              />
              {errors.name && (
                <p id="doc-name-error" role="alert" className="mt-1 text-xs text-sev-expired">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="mb-4">
              <label
                htmlFor="doc-notes"
                className="block text-xs font-medium text-text-muted mb-1"
              >
                {t("documents:upload_field_notes")}
              </label>
              <textarea
                id="doc-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("documents:upload_field_notes_placeholder")}
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-border bg-bg text-base
                           focus:bg-surface focus:border-accent focus:outline-none
                           focus:ring-2 focus:ring-accent/20 resize-none min-h-[44px]"
                disabled={mutation.isPending}
              />
            </div>

            {/* File picker + drag-drop */}
            <div className="mb-5">
              <label htmlFor="doc-file" className="block text-xs font-medium text-text-muted mb-1">
                {t("documents:upload_field_file")}
                <span className="text-sev-expired ml-0.5" aria-hidden>*</span>
              </label>

              {/* Hidden native input — triggered by click on drop zone */}
              <input
                ref={fileInputRef}
                id="doc-file"
                type="file"
                accept={acceptAttr}
                onChange={handleInputChange}
                className="sr-only"
                aria-describedby={errors.file ? "doc-file-error" : "doc-file-hint"}
                aria-invalid={!!errors.file}
                disabled={mutation.isPending}
              />

              <div
                role="button"
                tabIndex={0}
                aria-controls="doc-file"
                onClick={() => !mutation.isPending && fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !mutation.isPending) {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={cn(
                  "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed",
                  "px-4 py-6 cursor-pointer transition-colors select-none",
                  "focus:outline-none focus:ring-2 focus:ring-accent/30",
                  isDragOver
                    ? "border-accent bg-accent/5"
                    : errors.file
                    ? "border-sev-expired/60 hover:border-sev-expired"
                    : "border-border hover:border-accent/60",
                  mutation.isPending && "pointer-events-none opacity-50",
                )}
              >
                <Upload className="h-6 w-6 text-text-dim" aria-hidden />
                {selectedFile ? (
                  <p className="text-sm text-text text-center break-all">
                    {t("documents:upload_file_selected", {
                      filename: selectedFile.name,
                      size: formatBytes(selectedFile.size, t),
                    })}
                  </p>
                ) : (
                  <p className="text-sm text-text-dim text-center">
                    {t("documents:upload_file_prompt")}
                  </p>
                )}
                <p id="doc-file-hint" className="text-xs text-text-dim text-center">
                  {t("documents:upload_file_accepted")}
                </p>
              </div>

              {errors.file && (
                <p id="doc-file-error" role="alert" className="mt-1 text-xs text-sev-expired">
                  {errors.file}
                </p>
              )}
            </div>

            {/* Upload progress bar */}
            {mutation.isPending && (
              <div className="mb-4" role="progressbar" aria-valuenow={uploadPercent} aria-valuemin={0} aria-valuemax={100}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-text-dim">
                    {t("documents:upload_progress_label")}
                  </span>
                  <span className="text-xs text-text-dim">{uploadPercent}%</span>
                </div>
                <div className="w-full bg-bg rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-accent h-1.5 rounded-full transition-all duration-200"
                    style={{ width: `${uploadPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={mutation.isPending}
                className="px-4 py-2 rounded-lg text-sm text-text-muted hover:bg-bg
                           min-h-[44px] focus:outline-none focus:ring-2 focus:ring-border
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("common:cancel")}
              </button>
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white
                           bg-accent hover:bg-accent/90 disabled:opacity-40
                           disabled:cursor-not-allowed min-h-[44px]
                           focus:outline-none focus:ring-2 focus:ring-accent/40"
              >
                {mutation.isPending
                  ? t("documents:upload_submitting_button")
                  : t("documents:upload_submit_button")}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
